import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { useRecoilState } from 'recoil';
import clsx from 'clsx';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SearchIcon from '@mui/icons-material/Search';
import { useSession, xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import {
  encodeSearchWord,
  firstDayOfMonth,
  getCurrentStartDate,
  getDateList,
  getFirstMonthOfYear,
  getMonthText,
  lastDayOfMonth,
  timestamp2Localestring,
} from '../../common/helper';
import {
  KEY_READ_TEXT,
  KEY_SCHOOL_READ_RANK_LIST,
  KEY_SCHOOL_WALK_RANK_LIST,
  KEY_SCHOOL_WRITE_RANK_LIST,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import excellog from '../../images/sub/excel_icon.svg';
import {
  getBookGroupList,
  getBookInfoList,
  registerBookGroup,
} from '../../common/api/ApiBook';
import { SwBookGroup } from '../commonComponent/SwBookGroup';
import { SwBookList } from '../commonComponent/SwBookList';
import { uploadFile } from '../../common/api/ApiUpload';
import { RESULT_OK } from '../../common/resultCode';
import { SwUserStatDetail } from '../commonComponent/SwUserStatDetail';
import { SchoolRankTable } from './SchoolRankTable';
import { SwSelectMonth } from '../commonComponent/SwSelectMonth';
import { SwSchoolStatTab } from '../commonComponent/SwSchoolStatTab';
import {
  getClassReadRankList,
  getClassWalkRankList,
  getClassWriteRankList,
} from '../../common/api/ApiClassStat';
import {
  downloadSchoolRankList,
  getSchoolReadRankList,
  getSchoolWalkRankList,
  getSchoolWriteRankList,
} from '../../common/api/ApiSchool';
import { selectSchoolInfoState } from '../../common/atom';

interface Props {}

export const SchoolRank: React.FC<Props> = () => {
  const { loginVal } = useSession();

  const [schoolClassInfo, setSchoolClassInfo] = useRecoilState(
    selectSchoolInfoState
  );
  const showSelectDay = false;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formInputRef = useRef<any>();
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestReadData, setRequestReadData] = useState<any>();
  const [requestWalkData, setRequestWalkData] = useState<any>();
  const [requestWriteData, setRequestWriteData] = useState<any>();
  const [resultFilter, setResultFilter] = useState<string>('');
  const [requestList, setRequestList] = useState<any>([]);
  const [bookGroupList, setBookGroupList] = useState<any>([]);
  const [bookInfoList, setBookInfoList] = useState<any>([]);
  const [isAdd, setIsAdd] = useState<boolean>(true);
  const [bookGroupSeq, setBookGroupSeq] = useState<number>(-1);
  const [selectedRow, setSelectedRow] = useState<number>(-1);
  const [targetMonth, setTargetMonth] = useState<any>();
  const rowsPerPageOptions = [5, 10, 15, 20];
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchKey, setSearchKey] = useState<string>('book-group');
  const [searchWord, setSearchWord] = useState<string>('');
  const [dailyStatType, setDailyStatType] = useState<any>(0);
  const [loading, setLoading] = useState<any>(false);
  const [downloadType, setDownloadType] = useState<any>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openSchoolClass, setOpenSchoolClass] = useState<boolean>(false);
  const [openUserStat, setOpenUserStat] = useState<boolean>(false);

  const {
    data,
    isLoading,
    refetch: refetchSchoolReadRank,
  } = useQuery(
    KEY_SCHOOL_READ_RANK_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          month: getMonthText(targetMonth * 1000),
          page: 0,
          rowsPerPage: 0,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
        };
        return getSchoolReadRankList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestReadData(res);
        } else {
          setRequestReadData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolWalkRank } = useQuery(
    KEY_SCHOOL_WALK_RANK_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          month: getMonthText(targetMonth * 1000),
          page: 0,
          rowsPerPage: 0,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
        };
        return getSchoolWalkRankList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestWalkData(res);
        } else {
          setRequestWalkData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolWriteRank } = useQuery(
    KEY_SCHOOL_WRITE_RANK_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          month: getMonthText(targetMonth * 1000),
          page: 0,
          rowsPerPage: 0,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
        };
        return getSchoolWriteRankList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestWriteData(res);
        } else {
          setRequestWriteData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const loadSchoolStat = () => {
    refetchSchoolReadRank();
    refetchSchoolWalkRank();
    refetchSchoolWriteRank();
  };

  const downloadSchoolRank = (newType: string) => {
    const newData: any = {
      type:
        newType === KEY_READ_TEXT
          ? 'read'
          : newType === KEY_WALK_TEXT
          ? 'walk'
          : 'write',
      schoolSeq: schoolClassInfo.schoolSeq,
      month: getMonthText(targetMonth * 1000),
      page: 0,
      rowsPerPage: 0,
      searchKey: searchKey,
      searchWord: encodeSearchWord(searchKey, searchWord),
    };
    downloadSchoolRankList(newData);
  };

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      loadSchoolStat();
    }
  }, [targetMonth]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      loadSchoolStat();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      const newTarget = getFirstMonthOfYear(loginVal);
      setTargetMonth(newTarget);

      setReqRequest(true);
      loadSchoolStat();
    }
  }, []);

  return (
    <>
      <Paper sx={{ p: '1.5rem' }}>
        <SwSchoolStatTab
          tabValue={1}
          schoolClassInfo={schoolClassInfo}
          setSchoolClassInfo={setSchoolClassInfo}
        />
        <SwSelectMonth
          onChangeMonth={item => {
            setTargetMonth(item);
          }}
          showSelectDay={showSelectDay}
        />
        {targetMonth && (
          <SchoolRankTable
            title={KEY_READ_TEXT}
            targetMonth={targetMonth}
            data={requestReadData}
            onDownload={() => downloadSchoolRank(KEY_READ_TEXT)}
          />
        )}
        <Divider sx={{ m: '', p: '' }} />
        {targetMonth && (
          <SchoolRankTable
            title={KEY_WALK_TEXT}
            targetMonth={targetMonth}
            data={requestWalkData}
            onDownload={() => downloadSchoolRank(KEY_WALK_TEXT)}
          />
        )}
        <Divider sx={{ m: '', p: '' }} />
        {targetMonth && (
          <SchoolRankTable
            title={KEY_WRITE_TEXT}
            targetMonth={targetMonth}
            data={requestWriteData}
            onDownload={() => downloadSchoolRank(KEY_WRITE_TEXT)}
          />
        )}
      </Paper>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      <SwUserStatDetail
        memberSeq={1}
        open={openUserStat}
        onClose={() => setOpenUserStat(false)}
      />
      {loading && <SwLoading />}
    </>
  );
};
